import React, { useState } from 'react';
import { BuildingOfficeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { CompanyService, CompanyCreateData } from '../../services/companyService';
import { Company } from '../../types';

interface CreateCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (company: Company) => void;
}

interface FormData {
  name: string;
  code: string;
  address: string;
  phone: string;
  email: string;
  contactPerson: string;
  subscriptionPlan: string;
}

interface ValidationErrors {
  name?: string;
  code?: string;
  address?: string;
  phone?: string;
  email?: string;
  contactPerson?: string;
}

const CreateCompanyModal: React.FC<CreateCompanyModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    code: '',
    address: '',
    phone: '',
    email: '',
    contactPerson: '',
    subscriptionPlan: 'basic',
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const companyService = React.useMemo(() => new CompanyService(), []);

  const resetForm = React.useCallback(() => {
    setFormData({
      name: '',
      code: '',
      address: '',
      phone: '',
      email: '',
      contactPerson: '',
      subscriptionPlan: 'basic',
    });
    setErrors({});
    setSubmitError('');
    setShowConfirmation(false);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleInputChange = (field: keyof FormData, value: string | number) => {
    // 電話番号の場合は数字とハイフンを許可（全角数字も許可）
    let processedValue = value;
    if (field === 'phone' && typeof value === 'string') {
      processedValue = value.replace(/[^0-9０-９-]/g, '');
    }

    setFormData(prev => ({ ...prev, [field]: processedValue }));

    // リアルタイムバリデーション
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePhoneBlur = () => {
    // 全角数字を半角数字に変換
    let convertedPhone = formData.phone.replace(/[０-９]/g, char =>
      String.fromCharCode(char.charCodeAt(0) - 65248)
    );

    // ハイフンを除去
    convertedPhone = convertedPhone.replace(/-/g, '');

    if (convertedPhone !== formData.phone) {
      setFormData(prev => ({ ...prev, phone: convertedPhone }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // 必須フィールドチェック
    if (!formData.name.trim()) {
      newErrors.name = '会社名は必須です';
    }

    // 会社コードが入力された場合のみフォーマットチェック
    if (formData.code.trim()) {
      if (!/^[a-zA-Z0-9_-]+$/.test(formData.code)) {
        newErrors.code = '会社コードは英数字、ハイフン、アンダースコアのみ使用可能です';
      } else if (formData.code.length < 2 || formData.code.length > 20) {
        newErrors.code = '会社コードは2文字以上20文字以下で入力してください';
      }
    }

    if (!formData.address.trim()) {
      newErrors.address = '住所は必須です';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = '電話番号は必須です';
    } else if (!/^[0-9０-９-]+$/.test(formData.phone)) {
      newErrors.phone = '電話番号は数字とハイフンのみで入力してください';
    } else {
      // ハイフンを除いた桁数チェック
      const digitsOnly = formData.phone
        .replace(/[０-９]/g, char => String.fromCharCode(char.charCodeAt(0) - 65248))
        .replace(/-/g, '');

      if (digitsOnly.length < 10 || digitsOnly.length > 11) {
        newErrors.phone = '電話番号は10桁または11桁で入力してください';
      }
    }

    // メールアドレス形式チェック
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '正しいメールアドレス形式で入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    if (!validateForm()) {
      setShowConfirmation(false);
      return;
    }

    setIsLoading(true);
    setSubmitError('');

    try {
      const companyData: CompanyCreateData = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        address: formData.address.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        contactPerson: formData.contactPerson.trim(),
        subscriptionPlan: formData.subscriptionPlan,
      };

      const createdCompany = await companyService.createCompany(companyData);

      onSuccess(createdCompany);
      onClose();
    } catch (error) {
      if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError('陸送会社の作成に失敗しました');
      }
      setShowConfirmation(false);
    } finally {
      setIsLoading(false);
    }
  };

  const subscriptionPlans = [
    { value: 'basic', label: 'ベーシック' },
    { value: 'standard', label: 'スタンダード' },
    { value: 'premium', label: 'プレミアム' },
    { value: 'enterprise', label: 'エンタープライズ' },
  ];

  if (showConfirmation) {
    return (
      <Modal isOpen={isOpen} onClose={() => setShowConfirmation(false)} title='陸送会社登録の確認'>
        <div className='space-y-6'>
          <div className='flex items-start space-x-3'>
            <ExclamationTriangleIcon className='h-6 w-6 text-amber-500 mt-1' />
            <div>
              <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
                以下の内容で陸送会社を登録します
              </h3>
              <div className='mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300'>
                <p>
                  <span className='font-medium'>会社名:</span> {formData.name}
                </p>
                <p>
                  <span className='font-medium'>会社コード:</span> {formData.code}
                </p>
                <p>
                  <span className='font-medium'>担当者:</span> {formData.contactPerson}
                </p>
                <p>
                  <span className='font-medium'>プラン:</span>{' '}
                  {subscriptionPlans.find(p => p.value === formData.subscriptionPlan)?.label}
                </p>
              </div>
            </div>
          </div>

          {submitError && (
            <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
              <p className='text-sm text-red-800 dark:text-red-200'>{submitError}</p>
            </div>
          )}

          <div className='flex justify-end space-x-3'>
            <Button
              variant='secondary'
              onClick={() => setShowConfirmation(false)}
              disabled={isLoading}
            >
              キャンセル
            </Button>
            <Button onClick={handleConfirmSubmit} loading={isLoading} disabled={isLoading}>
              {isLoading ? '作成中...' : '作成実行'}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title='新規陸送会社登録' size='lg'>
      <div className='space-y-6'>
        {submitError && (
          <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
            <div className='flex items-center'>
              <ExclamationTriangleIcon className='h-5 w-5 text-red-400 mr-2' />
              <p className='text-sm text-red-800 dark:text-red-200'>{submitError}</p>
            </div>
          </div>
        )}

        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          {/* 基本情報 */}
          <div className='space-y-4'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white flex items-center'>
              <BuildingOfficeIcon className='h-5 w-5 mr-2' />
              基本情報
            </h3>

            <Input
              label='会社名'
              value={formData.name}
              onChange={e => handleInputChange('name', e.target.value)}
              error={errors.name}
              required
              placeholder='例: 株式会社サンプル陸送'
            />

            <Input
              label='会社コード'
              value={formData.code}
              onChange={e => handleInputChange('code', e.target.value)}
              error={errors.code}
              placeholder='例: sample-trans（空欄の場合は自動生成）'
              helperText='英数字、ハイフン、アンダースコアのみ（2-20文字）。空欄の場合は会社名から自動生成されます。'
            />

            <Input
              label='担当者名'
              value={formData.contactPerson}
              onChange={e => handleInputChange('contactPerson', e.target.value)}
              error={errors.contactPerson}
              placeholder='例: 田中太郎'
            />
          </div>

          {/* 連絡先情報 */}
          <div className='space-y-4'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white'>連絡先情報</h3>

            <Input
              label='住所'
              value={formData.address}
              onChange={e => handleInputChange('address', e.target.value)}
              error={errors.address}
              required
              placeholder='例: 東京都渋谷区...'
            />

            <Input
              label='電話番号'
              type='tel'
              value={formData.phone}
              onChange={e => handleInputChange('phone', e.target.value)}
              onBlur={handlePhoneBlur}
              error={errors.phone}
              required
              placeholder='例: 03-1234-5678'
              helperText='数字とハイフンで入力可能。フォーカスアウト時に自動でハイフン除去・半角変換されます。'
            />

            <Input
              label='メールアドレス'
              type='email'
              value={formData.email}
              onChange={e => handleInputChange('email', e.target.value)}
              error={errors.email}
              placeholder='例: contact@sample-trans.com'
            />
          </div>
        </div>

        {/* アクションボタン */}
        <div className='flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700'>
          <Button variant='secondary' onClick={onClose} disabled={isLoading}>
            キャンセル
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            登録内容確認
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateCompanyModal;
